import React, { useEffect, useState } from 'react';
import { motion } from 'framer-motion';

const AnalyticsDashboard = () => {
  const [analyticsUrl, setAnalyticsUrl] = useState('');
  const [propertyId, setPropertyId] = useState('G-4HE642NZY0');

  useEffect(() => {
    // Construct the Firebase Analytics Dashboard URL
    const firebaseProjectId = 'cosmovex';
    const url = `https://console.firebase.google.com/project/${firebaseProjectId}/analytics`;
    setAnalyticsUrl(url);
  }, []);

  const quickLinks = [
    {
      title: 'Events',
      description: 'View all tracked events',
      url: `https://console.firebase.google.com/project/cosmovex/analytics/app/web:ZGFlYzVmYTMtNDliOC00ZTk0LWE4MDAtOGQ2YzQ3YjlhNjQy/events~2Frealtime`,
      icon: '📊',
    },
    {
      title: 'Real-time',
      description: 'See live user activity',
      url: `https://console.firebase.google.com/project/cosmovex/analytics/app/web:ZGFlYzVmYTMtNDliOC00ZTk0LWE4MDAtOGQ2YzQ3YjlhNjQy/realtime`,
      icon: '🔴',
    },
    {
      title: 'Engagement',
      description: 'User engagement metrics',
      url: `https://console.firebase.google.com/project/cosmovex/analytics/app/web:ZGFlYzVmYTMtNDliOC00ZTk0LWE4MDAtOGQ2YzQ3YjlhNjQy/engagement`,
      icon: '💡',
    },
    {
      title: 'User Attributes',
      description: 'Custom user properties',
      url: `https://console.firebase.google.com/project/cosmovex/analytics/app/web:ZGFlYzVmYTMtNDliOC00ZTk0LWE4MDAtOGQ2YzQ3YjlhNjQy/userproperties`,
      icon: '👤',
    },
  ];

  const customEvents = [
    { name: 'blog_view', description: 'Blog post views with title and category' },
    { name: 'category_filter', description: 'Blog category filter selections' },
    { name: 'share', description: 'Social media shares (LinkedIn, Twitter, Facebook)' },
    { name: 'copy_link', description: 'Link copy actions' },
    { name: 'related_blog_click', description: 'Related blog post clicks' },
    { name: 'blog_read_time', description: 'Time spent reading blog posts' },
    { name: 'newsletter_subscribe', description: 'Newsletter subscriptions' },
    { name: 'search', description: 'Blog search queries' },
  ];

  return (
    <div className="min-h-screen bg-[#FBFAF6] p-8">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          className="mb-8"
        >
          <h1 className="text-4xl font-bold text-[#26313B] mb-2">Analytics Dashboard</h1>
          <p className="text-[#5B6B7C]">
            View detailed analytics for your blog posts and user engagement
          </p>
        </motion.div>

        {/* Info Banner */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="mb-8 p-6 bg-gradient-to-r from-[#E8B44C]/15 to-[#E8B44C]/5 border border-[#E1DCCE] rounded-2xl"
        >
          <div className="flex items-start gap-4">
            <div className="text-3xl">ℹ️</div>
            <div>
              <h3 className="text-lg font-semibold text-[#26313B] mb-2">
                Analytics Protection Active
              </h3>
              <p className="text-sm text-[#5B6B7C] mb-2">
                Analytics tracking is automatically disabled when:
              </p>
              <ul className="list-disc list-inside text-sm text-[#5B6B7C] space-y-1 ml-4">
                <li>Running on localhost (development mode)</li>
                <li>Logged in as admin user (trionapps@gmail.com)</li>
              </ul>
              <p className="text-xs text-[#8A93A2] mt-3">
                This ensures your own activity doesn't affect the analytics data.
              </p>
            </div>
          </div>
        </motion.div>

        {/* Quick Links */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          className="mb-8"
        >
          <h2 className="text-2xl font-bold text-[#26313B] mb-4">Quick Access</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            {quickLinks.map((link, idx) => (
              <motion.a
                key={link.title}
                href={link.url}
                target="_blank"
                rel="noopener noreferrer"
                whileHover={{ y: -4 }}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.3 + idx * 0.05 }}
                className="group relative"
              >
                <div className="absolute -inset-[1px] bg-gradient-to-r from-[#E8B44C]/30 to-[#E8B44C]/10 rounded-xl opacity-0 group-hover:opacity-100 blur-sm transition-opacity" />
                <div className="relative bg-white rounded-xl border border-[#E1DCCE] group-hover:border-[#D9A32E] p-6 transition-all shadow-[0_1px_2px_rgba(52,44,26,.06),0_4px_14px_rgba(52,44,26,.05)]">
                  <div className="text-3xl mb-3">{link.icon}</div>
                  <h3 className="text-lg font-semibold text-[#26313B] mb-1">
                    {link.title}
                  </h3>
                  <p className="text-sm text-[#5B6B7C]">{link.description}</p>
                  <div className="mt-4 flex items-center text-xs text-[#A8720E] group-hover:text-[#7C540A] transition-colors">
                    Open in Firebase
                    <svg className="w-4 h-4 ml-1 group-hover:translate-x-1 transition-transform" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                    </svg>
                  </div>
                </div>
              </motion.a>
            ))}
          </div>
        </motion.div>

        {/* Custom Events Tracking */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.4 }}
          className="mb-8"
        >
          <h2 className="text-2xl font-bold text-[#26313B] mb-4">Custom Events Tracked</h2>
          <div className="bg-white rounded-2xl border border-[#E1DCCE] overflow-hidden shadow-[0_1px_2px_rgba(52,44,26,.06),0_4px_14px_rgba(52,44,26,.05)]">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-[#E1DCCE]">
                    <th className="px-6 py-4 text-left text-sm font-semibold text-[#5B6B7C]">
                      Event Name
                    </th>
                    <th className="px-6 py-4 text-left text-sm font-semibold text-[#5B6B7C]">
                      Description
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {customEvents.map((event, idx) => (
                    <motion.tr
                      key={event.name}
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      transition={{ delay: 0.5 + idx * 0.05 }}
                      className="border-b border-[#E1DCCE] hover:bg-[#F1EEE5] transition-colors"
                    >
                      <td className="px-6 py-4">
                        <code className="text-sm font-mono text-[#A8720E] bg-[#F1EEE5] px-2 py-1 rounded">
                          {event.name}
                        </code>
                      </td>
                      <td className="px-6 py-4 text-sm text-[#5B6B7C]">
                        {event.description}
                      </td>
                    </motion.tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </motion.div>

        {/* Main Dashboard Link */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.6 }}
        >
          <div className="bg-gradient-to-r from-[#E8B44C]/12 via-[#E8B44C]/6 to-[#E8B44C]/12 rounded-2xl border border-[#E1DCCE] p-8 text-center">
            <h2 className="text-2xl font-bold text-[#26313B] mb-3">
              Firebase Analytics Console
            </h2>
            <p className="text-[#5B6B7C] mb-6 max-w-2xl mx-auto">
              Access the full Firebase Analytics dashboard to view detailed reports,
              user demographics, conversion funnels, and more.
            </p>
            <a
              href={analyticsUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 px-6 py-3 bg-[#E8B44C] hover:bg-[#DFA93C] border border-[#D9A32E] text-[#221A06] font-semibold rounded-xl transition-all shadow-lg shadow-[#E8B44C]/30 hover:shadow-[#E8B44C]/50"
            >
              <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
              </svg>
              Open Firebase Analytics
            </a>
          </div>
        </motion.div>

        {/* Google Analytics Integration */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.7 }}
          className="mt-8"
        >
          <div className="bg-white rounded-2xl border border-[#E1DCCE] p-6 shadow-[0_1px_2px_rgba(52,44,26,.06),0_4px_14px_rgba(52,44,26,.05)]">
            <h3 className="text-lg font-semibold text-[#26313B] mb-3">
              Google Analytics Integration
            </h3>
            <p className="text-sm text-[#5B6B7C] mb-4">
              Your Firebase Analytics is linked to Google Analytics 4 property:
            </p>
            <div className="flex items-center gap-3 mb-4">
              <code className="text-sm font-mono text-[#2E7D4F] bg-[#F1EEE5] px-3 py-2 rounded flex-1">
                {propertyId}
              </code>
              <a
                href="https://analytics.google.com/"
                target="_blank"
                rel="noopener noreferrer"
                className="px-4 py-2 bg-white hover:bg-[#F1EEE5] border border-[#E1DCCE] hover:border-[#D9A32E] rounded-lg text-sm text-[#3D4B59] transition-all"
              >
                Open GA4
              </a>
            </div>
            <p className="text-xs text-[#8A93A2]">
              Use Google Analytics for advanced reporting, custom dashboards, and data exports.
            </p>
          </div>
        </motion.div>

        {/* Popular Metrics to Track */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.8 }}
          className="mt-8"
        >
          <h2 className="text-2xl font-bold text-[#26313B] mb-4">Key Metrics to Monitor</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="bg-white rounded-xl border border-[#E1DCCE] p-6 shadow-[0_1px_2px_rgba(52,44,26,.06),0_4px_14px_rgba(52,44,26,.05)]">
              <div className="text-3xl mb-3">📈</div>
              <h3 className="text-lg font-semibold text-[#26313B] mb-2">
                Popular Blog Posts
              </h3>
              <p className="text-sm text-[#5B6B7C]">
                Track which blog titles get the most views using the <code className="text-[#A8720E] bg-[#F1EEE5] px-1 rounded">blog_view</code> event
              </p>
            </div>
            <div className="bg-white rounded-xl border border-[#E1DCCE] p-6 shadow-[0_1px_2px_rgba(52,44,26,.06),0_4px_14px_rgba(52,44,26,.05)]">
              <div className="text-3xl mb-3">🏷️</div>
              <h3 className="text-lg font-semibold text-[#26313B] mb-2">
                Category Preferences
              </h3>
              <p className="text-sm text-[#5B6B7C]">
                See which categories users filter most using the <code className="text-[#A8720E] bg-[#F1EEE5] px-1 rounded">category_filter</code> event
              </p>
            </div>
            <div className="bg-white rounded-xl border border-[#E1DCCE] p-6 shadow-[0_1px_2px_rgba(52,44,26,.06),0_4px_14px_rgba(52,44,26,.05)]">
              <div className="text-3xl mb-3">⏱️</div>
              <h3 className="text-lg font-semibold text-[#26313B] mb-2">
                Reading Time
              </h3>
              <p className="text-sm text-[#5B6B7C]">
                Understand engagement with the <code className="text-[#A8720E] bg-[#F1EEE5] px-1 rounded">blog_read_time</code> event tracking
              </p>
            </div>
          </div>
        </motion.div>
      </div>
    </div>
  );
};

export default AnalyticsDashboard;
